import type { ComponentProps } from 'react';
import React from 'react';
import type { ViewProps, ViewStyle } from 'react-native';
import { StyleSheet, View } from 'react-native';
import { RectButton } from 'react-native-gesture-handler';
import Animated, {
  Extrapolate,
  interpolate,
  useAnimatedStyle,
} from 'react-native-reanimated';

import { theme, TYPE_CONFIG } from '@garnish/constants';

import { useResponsive } from '../../hooks';
import { slugify } from '../../utils';
import { TagAlert } from '../TagAlert';
import { TagLabel } from '../TagLabel';
import { BodyText } from '../Text';
import { Icon, IconLink } from '../Icon';
import { LoadingDots } from '../LoadingDots';
import { ProductIngredientsList } from '../ProductIngredientsList';
import { ShareIcon } from '../ShareIcon';
import { SwipeStatus, useCardGestures, type SwipeSnapPoints } from './hooks';
import { Card } from '../Card';
import { ProductCardImage } from './components';

export const ProductCard = React.memo((props: ProductCardProps) => {
  const {
    name,
    label,
    description,
    restrictionNotice,
    unavailableNotice,
    price,
    calories,
    quantity,
    favorited,
    cloudinaryImageSrc,
    imageSize = 'regular',
    addedIngredients = [],
    addedIngredientsLabel,
    removedIngredients = [],
    removedIngredientsLabel,
    FooterElement,
    accessibilityLabel,
    accessibilityHint,
    accessibilityRole = 'button',
    isDisabled,
    onCardPress,
    onSwipeEnd,
    onSwipeToAdd,
    onFavorite,
    onShare,
  } = props;

  const { currentBreakpoint } = useResponsive();
  const { titleStyle, descriptionSize, sizeStyles } =
    useConditionalCardStyles();

  const {
    handleGestureEvent,
    triggerCallback,
    status: swipeStatus,
    swipeDistance,
  } = useCardGestures({
    snapPoints: SNAP_POINTS,
    maxSwipeDistance: 0,
    minSwipeDistance: LONG_SNAP_POINT,
    onSwipeEnd,
    callback: onSwipeToAdd,
  });

  // 💡 card can be expanded only when corresponding callback is provided and screen size matches mobile breakpoint
  const cardCanBeExpanded = Boolean(onSwipeToAdd && currentBreakpoint.isXS);

  const animatedStyles = useAnimatedCardStyles(swipeDistance);
  const swipeCallbackIsRunning = swipeStatus === SwipeStatus.Busy;
  const cardIsExpanded =
    swipeStatus === SwipeStatus.Expanded || swipeCallbackIsRunning;
  const cardIsExpanding = swipeStatus === SwipeStatus.Expanding;

  const testIDPrefix = slugify(name);
  const wrapperTestID = `${testIDPrefix}-product-card`;
  const pressableTestID = `${testIDPrefix}-product-card-pressable`;
  const productNameTestID = `${testIDPrefix}-product-name`;

  const hasIngredientsList =
    addedIngredients.length + removedIngredients.length > 0;

  // ─────────────────────────────────────────────────────────────────

  return (
    <View
      style={styles.cardWrapper}
      pointerEvents={swipeCallbackIsRunning ? 'none' : 'auto'}
      accessibilityState={{ busy: swipeCallbackIsRunning }}
      testID={wrapperTestID}
    >
      {/* ========== swipe area (add to bag) ========== */}

      {cardCanBeExpanded ? (
        <AddToBag
          triggerCallback={triggerCallback}
          isExpanded={cardIsExpanded}
          name={name}
          isRunningCallback={swipeCallbackIsRunning}
          swipeAreaStyles={animatedStyles.swipeArea}
          swipeAreaIconStyles={animatedStyles.icon}
        />
      ) : null}

      {/* ========== card area ========== */}

      <Card
        onGestureEvent={cardCanBeExpanded ? handleGestureEvent : undefined}
        onPress={cardIsExpanding || swipeCallbackIsRunning ? null : onCardPress}
        style={[styles.pressableWrapper, sizeStyles, animatedStyles.card]}
        testID={pressableTestID}
        accessibilityRole={accessibilityRole}
        accessibilityLabel={accessibilityLabel}
        accessibilityHint={accessibilityHint}
        accessibilityState={
          cardIsExpanded ? { expanded: cardIsExpanded } : undefined
        }
        disabled={isDisabled}
      >
        <View style={styles.header}>
          {/* ========== label ========== */}

          <View style={styles.row}>
            {label ? (
              <View style={styles.labelWrapper}>
                <TagLabel
                  size={3}
                  palette={isDisabled ? 'tertiary' : 'primary'}
                >
                  {label}
                </TagLabel>
              </View>
            ) : null}

            {onFavorite ? (
              <IconLink
                width={24}
                height={24}
                style={styles.headerIcon}
                color={theme.colors.KALE}
                accessibilityLabel="favorite product"
                name={favorited ? 'IconHeartFill' : 'IconHeartStroke'}
                onPress={onFavorite}
              />
            ) : null}
          </View>

          <ProductCardImage
            size={imageSize}
            baseUrl={cloudinaryImageSrc ?? ''}
          />
        </View>

        {/* ========== product name ========== */}

        <BodyText
          size={2}
          testID={productNameTestID}
          bold={true}
          style={titleStyle}
        >
          {name}
        </BodyText>

        {/* ========== ========== ========== */}

        {hasIngredientsList || description ? (
          <View style={styles.ingredientsAndDescriptionWrapper}>
            {/* ========== ingredients ========== */}

            {hasIngredientsList ? (
              <View style={styles.ingredientsListWrapper}>
                <ProductIngredientsList
                  addedIngredients={addedIngredients}
                  addedIngredientsLabel={addedIngredientsLabel}
                  removedIngredients={removedIngredients}
                  removedIngredientsLabel={removedIngredientsLabel}
                  textSize={descriptionSize}
                />
              </View>
            ) : null}

            {/* ========== description ========== */}

            {description ? (
              <BodyText size={descriptionSize}>{description}</BodyText>
            ) : null}
          </View>
        ) : null}

        {quantity ? (
          <BodyText size={descriptionSize} style={styles.quantity}>
            {quantity}
          </BodyText>
        ) : null}

        {/* ========== restriction notice ========== */}

        {restrictionNotice ? (
          <View style={styles.restrictionNoticeWrapper}>
            <TagAlert size="medium" palette="caution">
              {restrictionNotice}
            </TagAlert>
          </View>
        ) : null}

        {/* ========== unavailable notice ========== */}
        {unavailableNotice ? (
          <View
            style={
              restrictionNotice
                ? styles.unavailableNoticeWrapper
                : styles.restrictionNoticeWrapper
            }
          >
            <TagAlert size="medium" palette="caution">
              {unavailableNotice}
            </TagAlert>
          </View>
        ) : null}

        <View style={styles.footer}>
          {/* ========== price and calories ========== */}

          {price ?? calories ? (
            <View>
              <TagLabel size={2} palette="secondary">
                <>
                  {price}
                  {price && calories ? ' — ' : ''}
                  {calories}
                </>
              </TagLabel>
            </View>
          ) : null}

          {onShare ? <ShareIcon onShare={onShare} /> : null}
        </View>

        {FooterElement}
      </Card>
    </View>
  );
});

// ─── TYPES ──────────────────────────────────────────────────────────────────────

export type ProductCardProps = Readonly<{
  name: string;
  price?: string;
  calories?: string;
  quantity?: string;
  favorited?: boolean;
  isDisabled?: boolean;

  /**
   * (e.g. "Seasonal", "Warm")
   */
  label?: string;

  /**
   * Product description or ingredients list
   */
  description?: string;

  /**
   * (e.g. "May contain gluten")
   */
  restrictionNotice?: string;

  /**
   * (e.g. "Product unavailable")
   */
  unavailableNotice?: string;

  /**
   * Product remote image URL (e.g. https://res.cloudinary.com/sweetgreen/image...)
   */
  cloudinaryImageSrc?: string;

  /**
   * How big is the image in the product card.
   */
  imageSize?: 'regular' | 'large';

  /**
   * Optional footer element.
   */
  FooterElement?: React.ReactNode;

  /**
   * Card click/press handler
   */
  onCardPress?: () => void;

  /**
   * End of swiping gesture on product card.
   */
  onSwipeEnd?: () => void;

  /**
   * Swipe to call an action handler
   */
  onSwipeToAdd?: (() => void) | (() => Promise<void>);

  /**
   * Share action for the product
   */
  onShare?: () => void;

  /**
   * Favorite action for the product
   */
  onFavorite?: () => void;
}> &
  ViewProps &
  Partial<IngredientsListProps>;

type IngredientsListProps = Pick<
  ComponentProps<typeof ProductIngredientsList>,
  | 'addedIngredients'
  | 'addedIngredientsLabel'
  | 'removedIngredients'
  | 'removedIngredientsLabel'
>;

type AddToBagProps = Readonly<{
  triggerCallback: () => void;
  name: string;
  swipeAreaStyles: ViewStyle;
  swipeAreaIconStyles: ViewStyle;
  isExpanded: boolean;
  isRunningCallback: boolean;
}>;

type BodyTextSize = ComponentProps<typeof BodyText>['size'];

// ─── STYLES ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  cardWrapper: {
    position: 'relative',
    flexGrow: 1,
  },

  swipeArea: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    flex: 1,
    borderRadius: theme.radius.medium,
    overflow: 'hidden',
  },
  swipeAreaInner: {
    width: '100%',
    height: '100%',
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  swipeAreaBackdrop: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: theme.colors.GREEN_5,
  },
  titleXS: {
    ...TYPE_CONFIG.BODY['18'],
  },
  pressableWrapper: {
    width: '100%',
    padding: theme.spacing['4'],
    flexGrow: 1,
  },
  pressableWrapperTabletUp: {
    padding: theme.spacing['6'],
  },
  header: {
    position: 'relative',
    alignItems: 'center',
    width: '100%',
    marginBottom: theme.spacing['4'],
  },
  headerIcon: {
    marginLeft: 'auto',
  },
  row: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  labelWrapper: {
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 1,
  },
  restrictionNoticeWrapper: {
    paddingTop: theme.spacing['4'],
  },
  unavailableNoticeWrapper: {
    paddingTop: theme.spacing['2'],
  },
  footer: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 'auto',
    paddingTop: theme.spacing['4'],
  },
  ingredientsAndDescriptionWrapper: {
    paddingTop: theme.spacing['2'],
  },
  ingredientsListWrapper: {
    paddingBottom: theme.spacing['1'],
  },
  quantity: {
    paddingTop: theme.spacing['1'],
  },
});

/**
 * There are 4 types of swiping:
 *
 * 1. Initial (not swiped)
 *
 *    ┌──────────────┐
 *    │              │
 *    │              │
 *    │     CARD     │
 *    │              │
 *    │              │
 *    └──────────────┘
 *
 * 2. Minimal swipe that doesn't trigger any actions
 *    and returns the card to the initial point (step 1)
 *
 *    ┌──────────────┬──┐
 *    │              │  │
 *    │              │  │
 *    │    < CARD    │  │
 *    │              │  │
 *    │              │  │
 *    └──────────────┴──┘
 *
 * 3. Short snap point that shows CTA area
 *
 *    ┌──────────────┬────┐
 *    │              │    │
 *    │              │    │
 *    │   << CARD    │    │
 *    │              │    │
 *    │              │    │
 *    └──────────────┴────┘
 *
 * 4. Long snap point that triggers the provided callback
 *
 *    ┌──────────────┬────────┐
 *    │              │        │
 *    │              │        │
 *    │   <<< CARD   │        │
 *    │              │        │
 *    │              │        │
 *    └──────────────┴────────┘
 */

// 💡 negative values are used because translations from right to left are calculated in negative numbers.

export const SWIPE_AREA_ICON_SIZE = 24;
export const SWIPE_AREA_WIDTH = 64;
export const SHORT_SNAP_POINT = -SWIPE_AREA_WIDTH / 2;
export const LONG_SNAP_POINT = -SWIPE_AREA_WIDTH * 2;
export const SNAP_POINTS: SwipeSnapPoints = [
  {
    width: 0,
    thresholds: { min: SHORT_SNAP_POINT + 0.01, max: 0 },
  },
  {
    width: -64,
    thresholds: { min: LONG_SNAP_POINT + 0.01, max: SHORT_SNAP_POINT },
  },
  {
    width: -128,
    thresholds: { min: Number.NEGATIVE_INFINITY, max: LONG_SNAP_POINT },
    shouldTriggerCallback: true,
  },
];

// ─── HOOKS ──────────────────────────────────────────────────────────────────────

/**
 * Returns styles based on viewport or hover/focus states.
 */
const useConditionalCardStyles = () => {
  const { match } = useResponsive();

  const descriptionSize = match([4, 3]) as BodyTextSize;
  const titleStyle = match([styles.titleXS, {}]);
  const sizeStyles = match([{}, styles.pressableWrapperTabletUp]);

  return {
    sizeStyles,
    titleStyle,
    descriptionSize,
  };
};

/**
 * Generates animated styles based on the swipe distance shared value.
 */
const useAnimatedCardStyles = (swipeDistance: Animated.SharedValue<number>) => {
  const card = useAnimatedStyle(() => {
    return {
      transform: [{ translateX: swipeDistance.value }],
    };
  }, []);

  const swipeArea = useAnimatedStyle(() => {
    const opacity = interpolate(
      swipeDistance.value,
      [LONG_SNAP_POINT, -SWIPE_AREA_WIDTH, 0],
      [1, 0, 0],
      { extrapolateLeft: Extrapolate.CLAMP },
    );

    return { opacity };
  });

  const icon = useAnimatedStyle(() => {
    const opacity = interpolate(
      swipeDistance.value,
      [-SWIPE_AREA_WIDTH, 0],
      [1, 0],
    );
    const iconTranslateX = swipeDistance.value / 2 + SWIPE_AREA_ICON_SIZE / 2;

    return {
      opacity,
      transform: [{ translateX: iconTranslateX }],
    };
  });

  return { card, swipeArea, icon };
};

// ─── SUBCOMPONENTS ──────────────────────────────────────────────────────────────

const AddToBag = React.memo(
  ({
    triggerCallback,
    name,
    swipeAreaStyles,
    swipeAreaIconStyles,
    isExpanded,
    isRunningCallback,
  }: AddToBagProps) => {
    return (
      <RectButton
        style={styles.swipeArea}
        onPress={triggerCallback}
        activeOpacity={1}
        underlayColor={theme.colors.GREEN_5}
        testID="product-card-add-to-bag-swipe-area"
      >
        <View
          style={styles.swipeAreaInner}
          focusable={isExpanded}
          accessibilityRole="button"
          accessibilityLabel={`${name}. Add "${name}" to bag.`}
          accessibilityHint="Adds product to bag with default ingredients."
          testID="product-card-add-to-bag-swipe-area-inner"
        >
          {/* ========== background backdrop ========== */}
          <Animated.View style={[styles.swipeAreaBackdrop, swipeAreaStyles]} />

          {/* ========== icons ========== */}
          <Animated.View style={swipeAreaIconStyles}>
            {isRunningCallback ? (
              <LoadingDots color={theme.colors.GREEN_1} />
            ) : (
              <Icon
                name="IconBadgePlus"
                width={SWIPE_AREA_ICON_SIZE}
                height={SWIPE_AREA_ICON_SIZE}
              />
            )}
          </Animated.View>
        </View>
      </RectButton>
    );
  },
);
